import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import type { Theme } from '@mui/material/styles';
import { css, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { CloseNotificationButton } from '@openx/components-core/lib/Snackbar/CloseNotificationButton.tsx';
import { SnackbarProvider } from '@openx/components-core/lib/Snackbar/SnackbarProvider.tsx';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initializeApp } from 'firebase/app';
import { type Auth, browserLocalPersistence, getAuth, setPersistence } from 'firebase/auth';
import type { SnackbarProvider as ISnackbarProvider, SnackbarOrigin } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router';
import { onQueryError } from './api/onQueryError.tsx';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary.tsx';
import { APP_VERSION } from './config.ts';
import { getEnvironmentConfigs } from './environmentConfigs.ts';
import { AuthProvider } from './modules/auth/components/AuthContext.tsx';
import { AppRoutes } from './routes/Routes.tsx';

const globalStyles = (theme: Theme) => css`
  * {
    font-family: "Figtree", sans-serif;
    margin: 0;
    padding: 0;
  }
  body {
    min-width: 1280px;
    color: ${theme.palette.text.primary};
    font-size: 14px;
  }
  a {
    color: ${theme.palette.primary.main};
    text-decoration: none;
  }
`;

const snackBarProps: SnackbarOrigin = {
  horizontal: 'left',
  vertical: 'bottom',
};

export const App = () => {
  const notistackRef = useRef<ISnackbarProvider>(null);
  const queryClient = useRef(
    new QueryClient({
      defaultOptions: { queries: { refetchOnWindowFocus: false } },
      queryCache: new QueryCache({
        onError: (error, query) => onQueryError(error, query, notistackRef.current),
      }),
    }),
  );
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState<Auth | undefined>(undefined);
  const [envConfig, setEnvConfig] = useState<ReturnType<typeof getEnvironmentConfigs>>();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const config = getEnvironmentConfigs();
        setEnvConfig(config);
        const app = initializeApp(config.firebase);
        const auth = getAuth(app);
        await setPersistence(auth, browserLocalPersistence);
        setAuth(auth);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: we want to log this error
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    // biome-ignore lint/suspicious/noConsole: we want to log this information
    console.info(`%cLogin version: ${APP_VERSION}`, 'font-weight:bold;');

    void initAuth();
  }, []);

  const onClickDismiss = (key: string | number) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  if (loading || !auth || !envConfig) {
    return null;
  }

  return (
    <StyledEngineProvider injectFirst>
      <QueryClientProvider client={queryClient.current}>
        {envConfig.env !== 'prod' && <ReactQueryDevtools initialIsOpen={false} />}

        <ThemeProvider theme={theme}>
          <SnackbarProvider
            ref={notistackRef}
            anchorOrigin={snackBarProps}
            action={(key) => <CloseNotificationButton action={onClickDismiss(key)} />}
          >
            <BrowserRouter>
              <GlobalStyles styles={globalStyles} />
              <CssBaseline />
              <ErrorBoundary>
                <AuthProvider auth={auth}>
                  <AppRoutes />
                </AuthProvider>
              </ErrorBoundary>
            </BrowserRouter>
          </SnackbarProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StyledEngineProvider>
  );
};
